import { normalize, schema } from 'normalizr';
import {
  __,
  assoc,
  assocPath,
  clone,
  concat,
  contains,
  difference,
  equals,
  evolve,
  findLastIndex,
  indexOf,
  insertAll,
  isEmpty,
  keys,
  last,
  merge,
  nth,
  omit,
  pickBy,
  pipe,
  prepend,
  reject,
  reverse,
  sort,
  subtract,
  uniq,
  without,
} from 'ramda';
import { withReducer } from 'recompose';
import { isFolder } from '../../media-manager-sdk/items';
import { getItem, selectItemToJump } from './withItemsReducer.selectors';

import { ItemType } from '../../../constants/ItemType';
import { MainSource } from '../../../constants/Source';
import { MediaManagerSDKAnyItem, MediaManagerSDKFolder } from '../../media-manager-sdk';
import { ItemsAction, SelectedItemType, TEntities, TItemsReducerAction, TState } from './withItemsReducer.types';

const itemSchema = new schema.Entity('items');

const normalizeItem = (item) => normalize(item, itemSchema);
const normalizeItems = (items) => normalize(items, [itemSchema]);
const mergeItemsEntities = (items) => merge(__, items);

const emptyItems = [];
const emptySelectedItems = [];
const emptyLastSelectedItems = [];

const initialState = ({ items, isLoading = false }): TState => {
  const { entities, result } = normalizeItems(items);

  if (!entities.items) {
    entities.items = {};
  }

  return {
    entities: {
      items: entities.items,
      relatedItems: {},
    },
    items: result,
    isLoading,
    relatedItems: [],
    selectedItems: emptySelectedItems,
    selectedItemTypes: {},
    editingItem: null,
    lastSelectedItemType: null,
    newFolderId: null,
    lastSelectedItemId: null,
    lastSelectedItems: emptyLastSelectedItems,
  };
};

const reducer = (state: TState, action: TItemsReducerAction): TState => {
  const { type, payload = {} } = action;
  const {
    entities,
    items,
    selectedItems,
    selectedItemTypes,
    editingItem,
    newFolderId,
    lastSelectedItemId,
    lastSelectedItems,
    lastSelectedItemType,
    relatedItems,
  } = state;

  switch (type) {
    case ItemsAction.Reset: {
      return initialState({
        items: payload.items,
        isLoading: payload.isLoading,
      });
    }

    case ItemsAction.SetRelatedItems: {
      const { entities, result } = normalizeItems(payload.relatedItems);

      if (!entities.items) {
        entities.items = {};
      }

      return {
        ...state,
        entities: {
          items: state.entities.items,
          relatedItems: entities.items,
        },
        relatedItems: result,
      };
    }

    case ItemsAction.AppendItems: {
      const normalized = normalizeItems(payload.items);

      return {
        ...state,
        entities: evolve<TEntities, TEntities>({ items: mergeItemsEntities(normalized.entities.items) }, entities),
        items: uniq([...items, ...normalized.result]),
      };
    }

    case ItemsAction.PrependItems: {
      const normalized = normalizeItems(payload.items);
      return {
        ...state,
        entities: evolve<TEntities, TEntities>({ items: mergeItemsEntities(normalized.entities.items) }, entities),
        items: uniq([...normalized.result, ...items]),
      };
    }

    case ItemsAction.InsertAfterFolders: {
      const normalized = normalizeItems(payload.items);
      const lastFolderIndex = findLastIndex((itemId) => isFolder(getItem(state)(itemId)), items);

      return {
        ...state,
        entities: evolve<TEntities, TEntities>({ items: mergeItemsEntities(normalized.entities.items) }, entities),
        items: uniq(insertAll<string, string>(lastFolderIndex + 1, normalized.result, items)),
      };
    }

    case ItemsAction.RemoveItems: {
      const itemsToRemove = normalizeItems(payload.items).result as string[];

      const newItems = difference(items, itemsToRemove);

      let newSelectedItems = [];

      if (payload.shouldSelectNextItem) {
        const firstSelectedItemIndex = indexOf(itemsToRemove[0], items);
        newSelectedItems = [newItems[firstSelectedItemIndex] ? newItems[firstSelectedItemIndex] : last(newItems)];
      }

      return {
        ...state,
        items: newItems,
        selectedItems: newSelectedItems,
        lastSelectedItemId: null,
        lastSelectedItems: emptyLastSelectedItems,
        selectedItemTypes: omit(itemsToRemove, selectedItemTypes),
      };
    }

    case ItemsAction.ClearItemsSelection: {
      const newState = { ...state, lastSelectedItemId: null, selectedItemTypes: {} };

      if (!isEmpty(selectedItems)) {
        newState.selectedItems = emptySelectedItems;
      }

      if (!isEmpty(lastSelectedItems)) {
        newState.lastSelectedItems = emptyLastSelectedItems;
      }

      return newState;
    }

    case ItemsAction.SelectItems: {
      const { itemIds, isRelatedItem } = payload;

      if (equals(itemIds, selectedItems) || (!!itemIds.length && !state.items.length)) {
        return state;
      }

      const selectedItemType = isRelatedItem ? SelectedItemType.RelatedItem : SelectedItemType.Item;
      const newSelectedItemTypes = { ...selectedItemTypes };

      itemIds.forEach((id) => {
        newSelectedItemTypes[id] = selectedItemType;
      });

      return {
        ...state,
        selectedItems: itemIds,
        lastSelectedItemId: itemIds[0],
        lastSelectedItemType: selectedItemType,
        selectedItemTypes: newSelectedItemTypes,
      };
    }

    case ItemsAction.SelectAllItems: {
      const isLastSelectedRelatedItem = lastSelectedItemType === SelectedItemType.RelatedItem;
      const newSelectedItems = isLastSelectedRelatedItem ? relatedItems : items;

      if (equals(newSelectedItems, selectedItems)) {
        return state;
      }

      const newSelectedItemTypes = {};

      newSelectedItems.forEach((id) => {
        newSelectedItemTypes[id] = lastSelectedItemType;
      });

      return {
        ...state,
        selectedItems: newSelectedItems,
        lastSelectedItemId: newSelectedItems[0],
        selectedItemTypes: newSelectedItemTypes,
      };
    }

    case ItemsAction.SelectItemsUntil: {
      const { itemId, isRelatedItem } = payload;
      const listOfItems = isRelatedItem ? relatedItems : items;

      const itemIndices = sort(subtract, [
        indexOf(itemId, listOfItems),
        lastSelectedItemId ? indexOf(lastSelectedItemId, listOfItems) : 0,
      ]);
      const itemsToSelect = listOfItems.slice(itemIndices[0], itemIndices[1] + 1);

      const selectedItemType = isRelatedItem ? SelectedItemType.RelatedItem : SelectedItemType.Item;

      const newSelectedItemTypes = { ...selectedItemTypes };
      itemsToSelect.forEach((id) => {
        newSelectedItemTypes[id] = selectedItemType;
      });

      return {
        ...state,
        selectedItems: pipe(difference(__, lastSelectedItems), concat(__, itemsToSelect), uniq)(selectedItems),
        lastSelectedItems: itemsToSelect,
        lastSelectedItemType: selectedItemType,
        selectedItemTypes: newSelectedItemTypes,
      };
    }

    case ItemsAction.EditItemCancel:
      return { ...state, editingItem: null };

    case ItemsAction.EditItemStart: {
      const editingItem = clone(entities.items[payload.itemId]);

      return { ...state, editingItem };
    }

    case ItemsAction.ClearAll:
      return initialState({ items: emptyItems });

    case ItemsAction.EditItemComplete:
      return {
        ...state,
        entities: assocPath(['items', editingItem.id], editingItem, entities),
        editingItem: null,
      };

    case ItemsAction.CreateNewFolderStart: {
      const { newFolderId } = payload;
      const editingItem: MediaManagerSDKFolder = {
        id: newFolderId,
        name: '',
        isNew: true,
        kind: ItemType.Folder,
        source: MainSource.SiteFiles,
        siteId: null,
      };
      const normalized = normalizeItem(editingItem);

      return {
        ...state,
        entities: evolve<TEntities, TEntities>(
          {
            items: pipe(assoc(newFolderId, editingItem), mergeItemsEntities(normalized.entities.items)),
          },
          entities,
        ),
        items: prepend(newFolderId, items),
        newFolderId,
        editingItem,
      };
    }

    case ItemsAction.CreateNewFolderCancel: {
      const { id } = editingItem;

      return {
        ...state,
        items: difference(items, [id]),
        entities: evolve<TEntities, TEntities>({ items: omit([id]) }, entities),
        editingItem: null,
        newFolderId: null,
      };
    }

    case ItemsAction.JumpToNextItem: {
      const { withShift, jumpTo, activeRelatedItemsState } = payload;

      const isLastSelectedRelatedItem = lastSelectedItemType === SelectedItemType.RelatedItem;
      const selectedItemType = isLastSelectedRelatedItem ? SelectedItemType.RelatedItem : SelectedItemType.Item;

      const itemList = isLastSelectedRelatedItem ? relatedItems : items;

      if (isEmpty(itemList)) {
        return state;
      }

      const nextItemId =
        withShift || (!withShift && selectedItems.length > 1) ? last(state.selectedItems) : selectedItems[0];

      const itemToJump = selectItemToJump({
        itemId: nextItemId,
        items: itemList,
        jumpTo,
        entities: {
          items: isLastSelectedRelatedItem ? entities.relatedItems : entities.items,
        },
        activeRelatedItemsState,
        isSelectingRelatedItem: isLastSelectedRelatedItem,
      });

      if (!itemToJump) {
        return state;
      }

      if (!withShift) {
        return {
          ...state,
          selectedItems: [itemToJump.id],
          selectedItemTypes: {
            [itemToJump.id]: selectedItemType,
          },
        };
      }

      const positionOfNewItem = indexOf(itemToJump.id, selectedItems);

      if (positionOfNewItem === -1) {
        const newSelection = [...selectedItems, itemToJump.id];

        const start = indexOf(nth(0, newSelection), itemList);
        const end = indexOf(last(newSelection), itemList);

        const newSelectedItemTypes = { ...selectedItemTypes };
        newSelection.forEach((id) => {
          newSelectedItemTypes[id] = selectedItemType;
        });

        return {
          ...state,
          selectedItemTypes: newSelectedItemTypes,
          selectedItems: start < end ? items.slice(start, end + 1) : reverse(itemList.slice(end, start + 1)),
        };
      }

      return {
        ...state,
        selectedItems: [...selectedItems.slice(0, positionOfNewItem), itemToJump.id],
      };
    }

    case ItemsAction.CreateNewFolderComplete: {
      const { updatedItem } = payload;
      const updatedFolderId = updatedItem.id;

      return {
        ...state,
        items: difference(prepend(updatedFolderId, items), [newFolderId]),
        // @ts-expect-error
        entities: evolve<TEntities, TEntities>(
          {
            items: pipe(omit([newFolderId]), assoc(updatedFolderId, updatedItem)),
          },
          entities,
        ),
        selectedItems: [updatedFolderId],
        selectedItemTypes: {
          [updatedFolderId]: SelectedItemType.Item,
        },
        newFolderId: null,
      };
    }

    case ItemsAction.UpdateEditingItem:
      return { ...state, editingItem: merge<MediaManagerSDKAnyItem, any>(editingItem, payload.updatedItem) };

    case ItemsAction.UpdateLocalItem: {
      const { itemId, updatedItem } = payload;
      const localItem = entities.items[itemId];

      return contains(itemId, items)
        ? {
            ...state,
            entities: assocPath(['items', itemId], merge(localItem, updatedItem), entities),
          }
        : state;
    }

    case ItemsAction.AddItemToSelection:
      const selectedItemType = payload.isRelatedItem ? SelectedItemType.RelatedItem : SelectedItemType.Item;
      return {
        ...state,
        selectedItems: uniq([...selectedItems, payload.itemId]),
        lastSelectedItemId: payload.itemId,
        lastSelectedItemType: selectedItemType,
        selectedItemTypes: {
          ...selectedItemTypes,
          [payload.itemId]: selectedItemType,
        },
      };

    case ItemsAction.RemoveItemFromSelection:
      const newSelectedItems = reject(equals(payload.itemId), selectedItems);
      const newSelectedItemTypes = omit([payload.itemId], selectedItemTypes);
      const lastAvailableSelectedItemId = newSelectedItems[newSelectedItems.length - 1];
      const newLastSelectedItemType = newSelectedItemTypes[lastAvailableSelectedItemId] || SelectedItemType.Item;

      return {
        ...state,
        lastSelectedItemType: newLastSelectedItemType,
        selectedItems: newSelectedItems,
        selectedItemTypes: newSelectedItemTypes,
      };

    case ItemsAction.ClearRelatedItemsSelection:
      const relatedSelectedItemIds = keys(
        pickBy((val) => val === SelectedItemType.RelatedItem, selectedItemTypes),
      ) as string[];

      return {
        ...state,
        selectedItemTypes: omit(relatedSelectedItemIds, selectedItemTypes),
        selectedItems: without(relatedSelectedItemIds, selectedItems),
        lastSelectedItemType: SelectedItemType.Item,
      };

    default:
      return state;
  }
};

export const reducerHOC = withReducer('itemsState', 'modifyItemsState', reducer, initialState);
